import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomDropdown } from '../../../common/custom-form/custom-dropdown.component';
import { TRANSLATIONS } from '../../../../utils/constant.utils';
import { TabberButton } from '../../../common/custom-form/tabber-button.component';
import { CustomMultiSelect } from '../../../common/custom-form/custom-multi-select.component';
import '../style/kyberconfig.style.css';
import { Button } from "react-bootstrap";
import { KyberConfigActions } from '../../../../store/common/chatbot/kyberconfig/kyberconfig.reducer';
import { NotificationModal } from '../../../common/modal/notification-modal.component';

const KyberConfigForm = ({
    roleOptions = [],
    selectedRoleIds = [],
    isEnabled = false,
    onEnabledChange
}) => {
    const dispatch = useDispatch();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [enableKyberAgents, setEnableKyberAgents] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState([]);

    // Original values loaded from the database.
    const [originalEnabled, setOriginalEnabled] = useState(false);
    const [originalRoleIds, setOriginalRoleIds] = useState([]);

    const tabberButtonRef = useRef(null);

    const { locale } = useSelector(state => state.translation);

    const [saveNotif, setSaveNotif] = useState({
        show: false,
        title: "",
        message: ""
    });

    /*
     * Convert saved RoleIds from the API into complete role objects.
     *
     * CustomMultiSelect expects selected option objects,
     * not only the RoleId values.
     */
    useEffect(() => {
        const selected = roleOptions
            .filter(role =>
                selectedRoleIds.some(
                    roleId => Number(roleId) === Number(role.Key)
                )
            )
            .map(role => role.Value);

        setSelectedRoles(selected);

        // Keep the original database RoleIds for change detection.
        setOriginalRoleIds(
            selectedRoleIds.map(Number).sort((a, b) => a - b)
        );
    }, [roleOptions, selectedRoleIds]);

    /*
     * Initialize/update the Kyber Agents toggle
     * using the value returned by the backend.
     */
    useEffect(() => {
        setEnableKyberAgents(isEnabled);

        // Keep the original database toggle value.
        setOriginalEnabled(isEnabled);

         // Sync master Kyber status with parent.
         onEnabledChange?.(isEnabled);
    }, [isEnabled]);

    const translator = (key, isCapitalize = true) => {
        if (locale === null || locale === '') {
            return TRANSLATIONS[
                TRANSLATIONS['defaultLocaleLanguage']
            ][key];
        }

        return isCapitalize
            ? TRANSLATIONS[locale][key]?.toUpperCase()
            : TRANSLATIONS[locale][key];
    };

    const handleToggle = () => {
        setIsCollapsed(prev => !prev);
    };

    /*
     * CustomMultiSelect returns the selected role objects.
     */
    const handleSelectedRoles = (roles) => {
        setSelectedRoles(roles || []);
    };

    /*
     * Saves:
     * - Enable Kyber Agents toggle
     * - Selected RoleIds
     */
    const handleSave = async () => {
        const roleIds = roleOptions
            .filter(role =>
                selectedRoles.includes(role.Value)
            )
            .map(role => Number(role.Key));

        const result = await dispatch(
            KyberConfigActions.saveKyberAgentConfig(
                enableKyberAgents,
                roleIds
            )
        );

        /*
        * Show notification based on API result.
        * Your backend currently returns PascalCase properties.
        */
        if (result?.Success === true) {
            toggleSaveNotif(
                true,
                translator('success_modal_title', false),
                translator('kyber_config_updated', false)
            );
        }
        else {
            toggleSaveNotif(
                true,
                translator('modal_error_title', false),
                result?.Message || translator('kyber_config_error', false)
            );
        }
    };

    const currentRoleIds = roleOptions
    .filter(role => selectedRoles.includes(role.Value))
    .map(role => Number(role.Key))
    .sort((a, b) => a - b);

    const rolesChanged =
        JSON.stringify(currentRoleIds) !==
        JSON.stringify(originalRoleIds);

    const toggleChanged =
        enableKyberAgents !== originalEnabled;

    const hasChanges = rolesChanged || toggleChanged;

    /*
    * Opens or closes the Save Changes notification modal.
    */
    const toggleSaveNotif = (isShow, title = "", message = "") => {
        setSaveNotif({
            show: isShow,
            title: title,
            message: message
        });
    };

    return (
        <div className="kyberconfig-form-container">
            <CustomDropdown
                activeDropdown={isCollapsed}
                toggleActiveState={handleToggle}
                label={translator("kyber_agents", false)}
                toggleStatus={enableKyberAgents}
            >
                <div className="kyberconfig-form-content">
                    <div className="kyberconfig-toggle-row">

                     <TabberButton
                        key={enableKyberAgents ? 'kyber-on' : 'kyber-off'}
                        ref={tabberButtonRef}
                        title={translator('enabled_kyber_agents', false)}
                        value={enableKyberAgents}
                        onToggle={(isActive) => {
                            setEnableKyberAgents(isActive);

                            // Immediately enable/disable access to child toggles.
                            onEnabledChange?.(isActive);
                        }}
                    />

                        <CustomMultiSelect
                            options={roleOptions}
                            label={translator('roles_enabled', false)}
                            placeholder={translator('select_placeholder', false)}
                            requiredField={true}
                            isDisabled={!enableKyberAgents}
                            fieldProps="kyberConfigFormA.ListIds"
                            onChange={handleSelectedRoles}
                            selectedOptions={selectedRoles}
                        />

                    </div>

                    <NotificationModal
                        show={saveNotif.show}
                        title={saveNotif.title}
                        message={saveNotif.message}
                        handleModal={() => toggleSaveNotif(false)}
                        customClass={'comment-notification-modal modal-text-bold notification-footer'}
                    />

                    <div className="save-btn-container">
                        <div className="tooltip-container">
                            <div className="btn-tooltiptext">

                                <Button
                                    className="save-details-btn btn btn-primary"
                                    disabled={
                                        !hasChanges ||
                                        (enableKyberAgents && selectedRoles.length === 0)
                                    }
                                    type="button"
                                    onClick={handleSave}
                                >
                                    Save Changes
                                </Button>

                                <div className="save-tooltip-text-container">
                                    <span className="tooltip-text-save">
                                        {translator("save_changes", false)}
                                    </span>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </CustomDropdown>
        </div>
    );
};

export default KyberConfigForm;